/**
 * Business Types for Burkina Faso market
 *
 * Defines configurable business types with their associated product categories.
 * Used for UI customization and default configuration based on business nature.
 */
/**
 * All business types adapted to the Burkina Faso market
 */
export const BUSINESS_TYPES = {
    // ═══════════════════════════════════════════════════════════════════════════
    // Commerce
    // ═══════════════════════════════════════════════════════════════════════════
    /** Commerce de détail */
    DETAIL: {
        code: "DETAIL",
        label: "Commerce de détail",
        description: "Vente au détail générale",
        categories: [
            "ALIMENTATION",
            "BOISSONS_NA",
            "BOISSONS_ALC",
            "HYGIENE",
            "TEXTILE",
            "ELECTRONIQUE",
            "COSMETIQUE",
            "DIVERS",
        ],
        defaultPosMode: true,
        psvbApplicable: true,
    },
    /** Commerce de gros */
    GROS: {
        code: "GROS",
        label: "Commerce de gros",
        description: "Vente en gros",
        categories: [
            "ALIMENTATION",
            "BOISSONS_NA",
            "BOISSONS_ALC",
            "HYGIENE",
            "TEXTILE",
            "ELECTRONIQUE",
            "COSMETIQUE",
            "DIVERS",
        ],
        defaultPosMode: false,
        psvbApplicable: true,
    },
    /** Demi-gros */
    DEMI_GROS: {
        code: "DEMI_GROS",
        label: "Demi-gros",
        description: "Vente en demi-gros",
        categories: [
            "ALIMENTATION",
            "BOISSONS_NA",
            "BOISSONS_ALC",
            "HYGIENE",
            "TEXTILE",
            "ELECTRONIQUE",
            "COSMETIQUE",
            "DIVERS",
        ],
        defaultPosMode: false,
        psvbApplicable: true,
    },
    /** Alimentation générale */
    ALIMENTATION: {
        code: "ALIMENTATION",
        label: "Alimentation générale",
        description: "Épicerie, superette",
        categories: [
            "ALIMENTATION",
            "BOISSONS_NA",
            "BOISSONS_ALC",
            "HYGIENE",
            "DIVERS",
        ],
        defaultPosMode: true,
        psvbApplicable: true,
    },
    /** Boutique de quartier */
    BOUTIQUE: {
        code: "BOUTIQUE",
        label: "Boutique de quartier",
        description: "Commerce de proximité",
        categories: [
            "ALIMENTATION",
            "BOISSONS_NA",
            "BOISSONS_ALC",
            "HYGIENE",
            "DIVERS",
        ],
        defaultPosMode: true,
        psvbApplicable: true,
    },
    // ═══════════════════════════════════════════════════════════════════════════
    // Spécialisé
    // ═══════════════════════════════════════════════════════════════════════════
    /** Pharmacie */
    PHARMACIE: {
        code: "PHARMACIE",
        label: "Pharmacie",
        description: "Officine pharmaceutique",
        categories: [
            "MEDICAMENT",
            "LAIT_INFANTILE",
            "COUCHES",
            "HYGIENE_BEBE",
            "COSMETIQUE_PHARMA",
            "PARAPHARMACIE",
            "MATERIEL_MED",
            "COMPLEMENT",
        ],
        defaultPosMode: true,
        psvbApplicable: false,
    },
    /** Librairie / Papeterie */
    LIBRAIRIE: {
        code: "LIBRAIRIE",
        label: "Librairie / Papeterie",
        description: "Livres et fournitures",
        categories: [
            "LIVRES",
            "FOURNITURES",
            "PAPETERIE",
            "INFORMATIQUE",
            "JEUX_JOUETS",
        ],
        defaultPosMode: false,
        psvbApplicable: false,
    },
    /** Quincaillerie */
    QUINCAILLERIE: {
        code: "QUINCAILLERIE",
        label: "Quincaillerie",
        description: "Matériaux et outillage",
        categories: ["MATERIAUX", "OUTILLAGE", "PLOMBERIE", "ELECTRICITE", "PEINTURE"],
        defaultPosMode: false,
        psvbApplicable: false,
    },
    /** Station-service */
    STATION: {
        code: "STATION",
        label: "Station-service",
        description: "Carburants et lubrifiants",
        categories: ["ESSENCE", "GASOIL", "LUBRIFIANT", "GAZ", "ACCESSOIRES_AUTO"],
        defaultPosMode: true,
        psvbApplicable: false,
    },
    // ═══════════════════════════════════════════════════════════════════════════
    // Restauration
    // ═══════════════════════════════════════════════════════════════════════════
    /** Restaurant / Maquis */
    RESTAURANT: {
        code: "RESTAURANT",
        label: "Restaurant / Maquis",
        description: "Restauration et boissons",
        categories: ["PLAT", "BOISSONS_NA_REST", "BOISSONS_ALC_REST", "SERVICE_REST"],
        defaultPosMode: true,
        psvbApplicable: false, // Configurable at business setup
    },
    // ═══════════════════════════════════════════════════════════════════════════
    // Services
    // ═══════════════════════════════════════════════════════════════════════════
    /** Cyber café / Services */
    CYBER: {
        code: "CYBER",
        label: "Cyber café / Services",
        description: "Services numériques",
        categories: ["NAVIGATION", "IMPRESSION", "SAISIE", "TRANSFERT"],
        defaultPosMode: false,
        psvbApplicable: false,
    },
    /** Atelier couture */
    COUTURE: {
        code: "COUTURE",
        label: "Atelier couture",
        description: "Confection textile",
        categories: ["CONFECTION", "RETOUCHE", "TEXTILE"],
        defaultPosMode: false,
        psvbApplicable: false,
    },
    /** Salon de coiffure */
    SALON: {
        code: "SALON",
        label: "Salon de coiffure",
        description: "Services esthétiques",
        categories: ["COIFFURE", "SOINS", "PRODUITS_BEAUTE"],
        defaultPosMode: false,
        psvbApplicable: false,
    },
};
/**
 * Get business type by code
 */
export function getBusinessType(code) {
    return BUSINESS_TYPES[code];
}
/**
 * Get all business types that default to POS mode
 */
export function getPOSBusinessTypes() {
    return Object.values(BUSINESS_TYPES).filter((b) => b.defaultPosMode);
}
/**
 * Get all business types where PSVB withholding applies
 */
export function getPSVBBusinessTypes() {
    return Object.values(BUSINESS_TYPES).filter((b) => b.psvbApplicable);
}
/**
 * Get categories available for a business type
 */
export function getCategoriesForBusinessType(code) {
    return BUSINESS_TYPES[code].categories;
}
//# sourceMappingURL=business-types.js.map